import * as XLSX from 'xlsx-js-style';
import { buildPartnersDistribution } from './partnersEquityDistribution';

const AMOUNT_FORMAT = '#,##0.00_);(#,##0.00)';
const EQUITY_FORMAT = '0.00"%"';
const COLUMN_COUNT = 4;
const MIN_COLUMN_WIDTHS = [24, 14, 14, 16];

const COLORS = {
  primary: '2F4BFF',
  primaryDark: '1E308F',
  white: 'FFFFFF',
  stripe: 'F6F8FF',
  text: '1F2937',
  border: 'C3CBDB',
};

const thin = { style: 'thin', color: { rgb: COLORS.border } };
const allThin = { top: thin, bottom: thin, left: thin, right: thin };

const STYLES = {
  title: {
    font: { bold: true, sz: 14, color: { rgb: COLORS.primaryDark } },
    alignment: { horizontal: 'right', vertical: 'center' },
  },
  baseLabel: {
    font: { bold: true, sz: 12, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'right', vertical: 'center' },
  },
  header: {
    font: { bold: true, sz: 12, color: { rgb: COLORS.white } },
    fill: { fgColor: { rgb: COLORS.primary } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: allThin,
  },
  row: (striped) => ({
    font: { sz: 11, color: { rgb: COLORS.text } },
    ...(striped ? { fill: { fgColor: { rgb: COLORS.stripe } } } : {}),
    alignment: { horizontal: 'right', vertical: 'center' },
    border: allThin,
  }),
  total: {
    font: { bold: true, sz: 12, color: { rgb: COLORS.white } },
    fill: { fgColor: { rgb: COLORS.primaryDark } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: allThin,
  },
};

const setCell = (ws, r, c, value, style, format) => {
  const addr = XLSX.utils.encode_cell({ r, c });
  const isNumber = typeof value === 'number' && !Number.isNaN(value);
  ws[addr] = {
    t: isNumber ? 'n' : 's',
    v: isNumber ? value : String(value ?? ''),
    s: style,
    ...(isNumber && format ? { z: format } : {}),
  };
};

/**
 * Append a "חלוקה לשותפים" table (one blank row below the current content) to an
 * xlsx-js-style worksheet. No-op when there are no partners.
 */
export const appendPartnersEquityToSheet = (ws, partners, total) => {
  const { rows, total: base, totalEquity, totalAmount } = buildPartnersDistribution(partners, total);
  if (!ws || !rows.length) return ws;

  const range = ws['!ref']
    ? XLSX.utils.decode_range(ws['!ref'])
    : { s: { r: 0, c: 0 }, e: { r: -1, c: 0 } };
  const heights = ws['!rows'] || [];
  let r = range.e.r + 2;

  setCell(ws, r, 0, 'חלוקה לשותפים', STYLES.title);
  ws['!merges'] = [...(ws['!merges'] || []), { s: { r, c: 0 }, e: { r, c: COLUMN_COUNT - 1 } }];
  heights[r] = { hpt: 22 };
  r += 1;

  setCell(ws, r, 0, 'סה״כ לחלוקה', STYLES.baseLabel);
  setCell(ws, r, 1, base, STYLES.baseLabel, AMOUNT_FORMAT);
  heights[r] = { hpt: 18 };
  r += 1;

  ['שם השותף', 'ת״ז שותף', 'אחוז בעלות (%)', 'סה״כ'].forEach((label, c) => setCell(ws, r, c, label, STYLES.header));
  heights[r] = { hpt: 22 };
  r += 1;

  rows.forEach((row, index) => {
    const style = STYLES.row(index % 2 === 1);
    setCell(ws, r, 0, row.name, style);
    setCell(ws, r, 1, row.partnerId, style);
    setCell(ws, r, 2, row.equity, style, EQUITY_FORMAT);
    setCell(ws, r, 3, row.amount, style, AMOUNT_FORMAT);
    heights[r] = { hpt: 18 };
    r += 1;
  });

  setCell(ws, r, 0, 'סה״כ', STYLES.total);
  setCell(ws, r, 1, '', STYLES.total);
  setCell(ws, r, 2, totalEquity, STYLES.total, EQUITY_FORMAT);
  setCell(ws, r, 3, totalAmount, STYLES.total, AMOUNT_FORMAT);
  heights[r] = { hpt: 22 };

  ws['!rows'] = heights;
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r, c: Math.max(range.e.c, COLUMN_COUNT - 1) },
  });

  const cols = [...(ws['!cols'] || [])];
  MIN_COLUMN_WIDTHS.forEach((minWidth, c) => {
    cols[c] = { ...(cols[c] || {}), wch: Math.max(cols[c]?.wch || 0, minWidth) };
  });
  ws['!cols'] = cols;

  return ws;
};
